import { useState } from 'react';
import { useFormField } from '@cian/frontend-request-form-widget';
import { useDispatch } from 'react-redux';

import { TThunkDispatch } from '../../../../../types/redux';
import { useCancellationEffect } from '../../../../../utils/useCancellationEffect';
import { uploadFile } from '../../../../../actions/file';
import { IAttachedDocument } from '../../../../../types/requestFormValue';
import { useFileUploadTracking } from '../../../utils';
import { useUploadingState } from './useUploadingState';

export interface IUseMultipleFilesUploadFieldProps {
  name: string;
}

export const useMultipleFilesUploadField = (props: IUseMultipleFilesUploadFieldProps) => {
  const { trackSelectDocuments } = useFileUploadTracking();
  const { isLoading, waitForFinish, setIsLoading } = useUploadingState();

  const dispatch = useDispatch<TThunkDispatch>();

  /** Если уйти с шага, запрос должен быть отменен, иначе это нарушит флоу **/
  const cancellationToken = useCancellationEffect();
  /** После загрузки файлов следует перерендерить инпут ввода, для сброса значений инпута **/
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const {
    field: { value, name },
    meta: { error, touched },
    helpers: { setValue, setError, setTouched },
  } = useFormField<IAttachedDocument[] | null>({
    name: props.name,
    /** В случае, если загрузка в процессе, откладываем сабмит через асинхронную валидацию **/
    validate: waitForFinish,
  });

  const handleFileSelect = async (files: FileList) => {
    /* istanbul ignore next */
    if (!files.length || isLoading) {
      return;
    }

    trackSelectDocuments();
    const filesToUpload = Array.from(files);
    let newFilesList: IAttachedDocument[] = [];
    let errorMessage;

    setIsLoading(true);

    try {
      const fileUploadRequests = filesToUpload.map(file => dispatch(uploadFile({ file })));
      newFilesList = await Promise.all(fileUploadRequests);
    } catch (error) {
      errorMessage = error instanceof Error ? error.message : String(error);
    }

    if (cancellationToken.isCanceled()) {
      return;
    }

    if (errorMessage) {
      setError(errorMessage);
    } else {
      setError(undefined);
      setValue([...(value ?? []), ...newFilesList], true);
    }

    setTouched(true);
    setFileInputKey(Date.now());
    setIsLoading(false);
  };

  const handleFileRemove = async (idToDelete: number) => {
    /* istanbul ignore else */
    if (value) {
      const newValue = value.filter(({ id }) => id !== idToDelete);

      setValue(newValue.length ? newValue : null, true);
      setTouched(true);
      setError(undefined);
    }
  };

  return {
    isLoading,
    handleFileSelect,
    handleFileRemove,
    fileInputKey,
    error,
    touched,
    value,
    name,
  };
};
